import { useMemo } from "react";
import { AccountType } from "../../../../shared/types/accounts";
import AccountAvatar from "../../../AccountAvatar/AccountAvatar";
import { getPlatformNameType } from "../../../../utils/accounts";
import {
  doesAccountNeedApiReconnect,
  isAccountFailedForPosting,
  isAccountPostingAndInsightsEnabled,
} from "../../../../entities/accounts/libs/get-account-statuses";
import { useInstagramApiPublishingOn } from "../../../../utils/feature-toggle-utils";
import { TooltipWrapper } from "../../../../ui/TooltipWrapper";
import { getAccountLabel } from "../account-label";

interface AccountOptionProps {
  account: AccountType;
  className?: string;
}

/**
 * Reusable account row component
 * Shows account avatar with status indicators and account name
 */
export function AccountOption({ account, className }: AccountOptionProps) {
  const name = getAccountLabel(account);
  const { platformType, platformName } = getPlatformNameType(account);

  const instagramApiPublishing = useInstagramApiPublishingOn();

  // Check if account is fully connected and functional
  const isFullyConnected =
    !doesAccountNeedApiReconnect(account, { instagramApiPublishing }) &&
    isAccountPostingAndInsightsEnabled(account, { instagramApiPublishing });

  const disconnected = useMemo(() => {
    return (
      isAccountFailedForPosting(account, { instagramApiPublishing }) ||
      account?.promptApiReconnect
    );
  }, [account, instagramApiPublishing]);

  return (
    <div className={`flex items-center w-full min-w-0 ${className || ""}`}>
      <AccountAvatar
        profilePictureUrl={account.profilePictureUrl}
        platformType={platformType}
        color={account.color}
        size={"small"}
        placement={"top"}
        tooltip={platformName}
        disconnected={disconnected || !isFullyConnected}
        useYtRedPlayIcon
        className="mr-2 flex-shrink-0"
      />
      <TooltipWrapper tooltip={name} className="min-w-0">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
          {name}
        </span>
      </TooltipWrapper>
    </div>
  );
}
